
import React, { useEffect, useState, DragEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getErrorMessage } from '../services/supabaseClient';
import { ApiService } from '../services/api';
import { Analytics } from '../services/analytics';
import { DriverInvoiceService } from '../services/driverInvoiceService';
import { RecoveryDecisionService } from '../services/recoveryDecisionService';
import { FleetTrialState, Ticket, Hire, Driver } from '../types';
import { useToast } from '../context/ToastContext';
import { useSubscription } from '../context/SubscriptionContext';
import Modal from '../components/Modal';
import TicketUploadModal from '../components/TicketUploadModal';
import ManualTicketModal from '../components/ManualTicketModal';
import CreateInvoiceModal from '../components/CreateInvoiceModal';
import { getStageDisplayLabel } from '../utils/stage';
import { inferEnforcementType } from '../utils/enforcement';
import { formatTicketStatus, normalizeTicketStatus } from '../utils/ticketStatus';
import {
  Loader2,
  AlertCircle,
  Search,
  Filter,
  FileText,
  Calendar,
  Car,
  UserCheck,
  Check,
  ArrowRight,
  AlertTriangle,
  Trash2,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  UploadCloud,
  Layers,
  MapPin,
  Lock,
  Upload,
  Receipt,
  Clock
} from 'lucide-react';

type TabView = 'actionRequired' | 'all';

const Tickets: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();
  const { checkAccess, promptUpgrade } = useSubscription();
  const hasBulkUpload = checkAccess('bulkUpload');
  
  // View State
  const [activeTab, setActiveTab] = useState<TabView>('actionRequired');
  
  // Data State
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filter/Sort/Page State
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('All');
  const [filterStage, setFilterStage] = useState<string>('All');
  const [monthFilter, setMonthFilter] = useState<string>('all'); // all | this | last | YYYY-MM
  const [groupByMonth, setGroupByMonth] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [sortBy, setSortBy] = useState<keyof Ticket>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const shouldGroupByMonth = groupByMonth;

  // Modal State
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [suggestedHires, setSuggestedHires] = useState<Hire[]>([]);
  const [otherHires, setOtherHires] = useState<Hire[]>([]);
  const [hireSearchTerm, setHireSearchTerm] = useState('');
  const [loadingHires, setLoadingHires] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [ticketToDelete, setTicketToDelete] = useState<Ticket | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [hireDriverByHireId, setHireDriverByHireId] = useState<Record<string, string>>({});
  const [invoiceContextLoaded, setInvoiceContextLoaded] = useState(false);
  const [loadingInvoiceContext, setLoadingInvoiceContext] = useState(false);
  const [creatingBulkInvoices, setCreatingBulkInvoices] = useState(false);
  const [selectedInvoiceTicketIds, setSelectedInvoiceTicketIds] = useState<string[]>([]);
  const [isCreateInvoiceModalOpen, setIsCreateInvoiceModalOpen] = useState(false);
  const [invoicePreselectedDriverId, setInvoicePreselectedDriverId] = useState<string | undefined>(undefined);
  const [invoicePreselectedTicketIds, setInvoicePreselectedTicketIds] = useState<string[]>([]);

  // Upload Modal State & DragDrop
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState<FileList | null>(null);
  const [isManualTicketModalOpen, setIsManualTicketModalOpen] = useState(false);
  const csvInputRef = React.useRef<HTMLInputElement>(null);
  const [isCsvImporting, setIsCsvImporting] = useState(false);
  const [csvImportReport, setCsvImportReport] = useState<{ success: number; merged?: number; errors: string[] } | null>(null);
  const [isCsvReportOpen, setIsCsvReportOpen] = useState(false);
  const [csvImportProgress, setCsvImportProgress] = useState<{ percent: number; message: string } | null>(null);
  const [trialState, setTrialState] = useState<FleetTrialState | null>(null);
  const [trialStateChecked, setTrialStateChecked] = useState(false);

  const trialLocked = Boolean(trialState?.locked);
  const trialLockedByLimit = trialState?.expired_by === 'pcn_limit';
  const trialLockMessage = trialLockedByLimit
    ? 'The 3-PCN trial limit has been reached. Review the trial snapshot or choose a plan to continue processing PCNs.'
    : 'The 7-day FleetMode trial has ended. Existing PCNs stay visible, but new uploads and manual tickets are paused until a plan is selected or the workspace is reviewed.';

  const guardTrialOpen = () => {
    if (!trialLocked) return false;
    addToast(trialLockedByLimit ? 'Your 3-PCN snapshot is ready. Choose the next step from Reports.' : 'Your trial has ended. Choose a plan to continue processing PCNs.', 'info');
    return true;
  };

  const refreshTrialState = async () => {
    try {
      const state = await ApiService.getFleetTrialState();
      setTrialState(state);
    } catch (err) {
      console.warn('Unable to load FleetMode trial lock state:', err);
    } finally {
      setTrialStateChecked(true);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('upload') !== '1') return;
    if (!trialStateChecked) return;

    if (trialLocked) {
      addToast(trialLockedByLimit ? 'Your 3-PCN snapshot is ready. Uploads are paused.' : 'Your trial has ended. Uploads are paused.', 'info');
      params.delete('upload');
      navigate(
        {
          pathname: location.pathname,
          search: params.toString() ? `?${params.toString()}` : ''
        },
        { replace: true }
      );
      return;
    }

    if (!hasBulkUpload) {
      promptUpgrade();
    } else {
      setDroppedFiles(null);
      setIsUploadModalOpen(true);
      Analytics.track('tickets_upload_deeplink_opened');
    }

    params.delete('upload');
    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : ''
      },
      { replace: true }
    );
  }, [addToast, hasBulkUpload, location.pathname, location.search, navigate, promptUpgrade, trialLocked, trialLockedByLimit, trialStateChecked]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchTickets();
    }, 300);
    return () => clearTimeout(timer);
  }, [activeTab, searchTerm, filterStatus, filterStage, monthFilter, page, sortBy, sortOrder]);

  useEffect(() => {
    let active = true;
    ApiService.getFleetTrialState()
      .then((state) => {
        if (active) setTrialState(state);
      })
      .catch((err) => {
        console.warn('Unable to load FleetMode trial lock state:', err);
      })
      .finally(() => {
        if (active) setTrialStateChecked(true);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!shouldGroupByMonth) return;
    if (sortBy !== 'created_at') setSortBy('created_at');
    if (sortOrder !== 'desc') setSortOrder('desc');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldGroupByMonth]);

  const canInvoiceTicket = React.useCallback((ticket: Ticket) => {
    return RecoveryDecisionService.decideRecoveryForTicket(ticket, {
      drivers,
      hireDriverByHireId
    }).invoiceable;
  }, [drivers, hireDriverByHireId]);

  const loadInvoiceContext = React.useCallback(async (): Promise<{ drivers: Driver[]; hireMap: Record<string, string> } | null> => {
    if (invoiceContextLoaded) {
      return { drivers, hireMap: hireDriverByHireId };
    }
    setLoadingInvoiceContext(true);
    try {
      const [driverData, hiresData] = await Promise.all([
        ApiService.getDrivers(),
        ApiService.getHires()
      ]);

      const loadedDrivers = driverData || [];
      setDrivers(loadedDrivers);
      const hireMap: Record<string, string> = {};
      for (const hire of hiresData || []) {
        if (hire?.id && hire?.driver_id) hireMap[hire.id] = hire.driver_id;
      }
      setHireDriverByHireId(hireMap);
      setInvoiceContextLoaded(true);
      return { drivers: loadedDrivers, hireMap };
    } catch (err: any) {
      console.error('Error loading invoice context:', err);
      addToast(getErrorMessage(err), 'error');
      return null;
    } finally {
      setLoadingInvoiceContext(false);
    }
  }, [invoiceContextLoaded, drivers, hireDriverByHireId, addToast]);

  useEffect(() => {
    if (activeTab === 'actionRequired') {
      loadInvoiceContext();
    }
  }, [activeTab, loadInvoiceContext]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const statusParam = activeTab === 'actionRequired' ? 'action_required' : filterStatus;
      let createdFrom: string | undefined;
      let createdTo: string | undefined;

      if (activeTab === 'all' && monthFilter && monthFilter !== 'all') {
        const toUtcMonthStart = (year: number, monthIndex: number) =>
          new Date(Date.UTC(year, monthIndex, 1, 0, 0, 0, 0));

        const now = new Date();
        const currentYear = now.getUTCFullYear();
        const currentMonthIndex = now.getUTCMonth();

        let year = currentYear;
        let monthIndex = currentMonthIndex;

        if (monthFilter === 'this') {
          // current month already set
        } else if (monthFilter === 'last') {
          monthIndex = currentMonthIndex - 1;
          if (monthIndex < 0) {
            monthIndex = 11;
            year = currentYear - 1;
          }
        } else {
          const match = /^(\d{4})-(\d{2})$/.exec(monthFilter);
          if (match) {
            year = Number(match[1]);
            monthIndex = Number(match[2]) - 1;
          } else {
            year = currentYear;
            monthIndex = currentMonthIndex;
          }
        }

        if (Number.isFinite(year) && Number.isFinite(monthIndex) && monthIndex >= 0 && monthIndex <= 11) {
          const from = toUtcMonthStart(year, monthIndex);
          const to = toUtcMonthStart(year, monthIndex + 1);
          createdFrom = from.toISOString();
          createdTo = to.toISOString();
        }
      }

      const { data, count } = await ApiService.getTickets({
        status: statusParam,
        stage: filterStage,
        search: searchTerm,
        createdFrom,
        createdTo,
        page,
        limit,
        sortBy,
        sortOrder
      });

      setTickets(data || []);
      setTotalCount(count || 0);
      
      // Track list view
      Analytics.track('tickets_viewed', { 
        count: data?.length, 
        tab: activeTab,
        statusFilter: filterStatus 
      });

    } catch (err: any) {
      console.error('Error fetching tickets:', err);
      const msg = getErrorMessage(err);
      if (msg === 'User not authenticated') {
        navigate('/login');
        return;
      }
      setError(msg);
      addToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  // --- Drag and Drop Handlers ---
  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (trialLocked) return;
    if (hasBulkUpload) setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    if (guardTrialOpen()) return;

    if (!hasBulkUpload) {
      promptUpgrade();
      return;
    }

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setDroppedFiles(e.dataTransfer.files);
      setIsUploadModalOpen(true);
      Analytics.track('tickets_files_dropped', { count: e.dataTransfer.files.length });
    }
  };

  const handleUploadClick = () => {
    if (guardTrialOpen()) return;
    if (!hasBulkUpload) {
      promptUpgrade();
      return;
    }
    setDroppedFiles(null);
    setIsUploadModalOpen(true);
  };

  const handleCsvImportClick = () => {
    if (guardTrialOpen()) return;
    if (!hasBulkUpload) {
      promptUpgrade();
      return;
    }
    csvInputRef.current?.click();
  };

  const handleManualTicketClick = () => {
    if (guardTrialOpen()) return;
    setIsManualTicketModalOpen(true);
  };

  const handleCsvFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsCsvImporting(true);
    setCsvImportProgress({ percent: 0, message: 'Starting…' });
    try {
      const report = await ApiService.importTicketsFromCsv(file, {
        onProgress: (p) => setCsvImportProgress(p)
      });
      setCsvImportReport(report);
      setIsCsvReportOpen(true);
      if (report.errors.length) {
        addToast(
          `Imported ${report.success} ticket(s)` +
            (report.merged ? `, merged ${report.merged} duplicate(s)` : '') +
            ` with ${report.errors.length} issue(s)`,
          'info'
        );
      } else {
        addToast(
          `Successfully imported ${report.success} ticket(s)` + (report.merged ? `, merged ${report.merged} duplicate(s)` : ''),
          'success'
        );
      }
      if (report.success > 0) await fetchTickets();
    } catch (err: any) {
      addToast(`CSV import failed: ${getErrorMessage(err)}`, 'error');
    } finally {
      setIsCsvImporting(false);
      setCsvImportProgress(null);
      if (csvInputRef.current) csvInputRef.current.value = '';
    }
  };

  const handleSort = (field: keyof Ticket) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      if (['issue_date', 'contravention_date', 'amount', 'created_at'].includes(field)) {
        setSortOrder('desc');
      } else {
        setSortOrder('asc');
      }
    }
    setPage(1);
  };

  const handleOpenDeleteModal = (ticket: Ticket) => {
    setTicketToDelete(ticket);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!ticketToDelete) return;
    setIsDeleting(true);
    try {
      await ApiService.deleteTicket(ticketToDelete.id);
      addToast('Ticket deleted', 'success');
      setIsDeleteModalOpen(false);
      setTicketToDelete(null);
      await fetchTickets();
    } catch (err: any) {
      addToast(getErrorMessage(err), 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  // --- Assignment Logic ---

  const handleOpenAssignModal = async (ticket: Ticket) => {
    setSelectedTicket(ticket);
    setIsModalOpen(true);
    setHireSearchTerm('');
    setSuggestedHires([]);
    setOtherHires([]);
    
    await fetchMatchingHires(ticket);
    Analytics.track('assignment_modal_opened', { ticketId: ticket.id });
  };

  const fetchMatchingHires = async (ticket: Ticket) => {
    try {
      setLoadingHires(true);
      const targetDate = ticket.contravention_date || ticket.issue_date;
      const data = await ApiService.suggestHiresForTicket({
        vrm: ticket.vrn,
        contraventionDate: targetDate,
        limit: 3
      });
      setSuggestedHires((data || []) as any);
    } catch (err) {
      console.error("Error finding matches", err);
      addToast("Failed to load suggested hires", 'error');
    } finally {
      setLoadingHires(false);
    }
  };

  const handleSearchHires = async (term: string) => {
    setHireSearchTerm(term);
    if (term.length < 2) {
      setOtherHires([]);
      return;
    }
    try {
      const data = await ApiService.searchHires(term);
      setOtherHires(data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleAssignHire = async (hire: Hire) => {
    if (!selectedTicket) return;
    setAssigning(true);

    try {
      await ApiService.updateTicket(selectedTicket.id, {
        hire_id: hire.id,
        assigned_driver_name: hire.driver_name,
        assigned_driver_email: hire.driver_email || undefined,
        status: 'active'
      });

      addToast("Ticket assigned successfully", 'success');
      Analytics.track('ticket_assigned', { 
        ticketId: selectedTicket.id, 
        hireId: hire.id,
        method: suggestedHires.find(h => h.id === hire.id) ? 'suggested' : 'manual'
      });
      setIsModalOpen(false);
      fetchTickets();
    } catch (err: any) {
      addToast(getErrorMessage(err), 'error');
    } finally {
      setAssigning(false);
    }
  };

  const resolveDriverIdForTicket = (
    ticket: Ticket,
    sourceDrivers: Driver[] = drivers,
    sourceHireMap: Record<string, string> = hireDriverByHireId
  ): string | null => {
    return RecoveryDecisionService.resolveDriverIdForRecovery(ticket, {
      drivers: sourceDrivers,
      hireDriverByHireId: sourceHireMap
    });
  };

  const createBulkInvoicesByDriver = async (
    entries: Array<{ ticket: Ticket; driverId: string }>,
    sourceDrivers: Driver[]
  ) => {
    const grouped = new Map<string, string[]>();
    for (const entry of entries) {
      const current = grouped.get(entry.driverId) || [];
      current.push(entry.ticket.id);
      grouped.set(entry.driverId, current);
    }

    if (grouped.size === 0) {
      addToast('No eligible tickets selected for invoicing', 'error');
      return;
    }

    setCreatingBulkInvoices(true);
    try {
      let successCount = 0;
      const failures: string[] = [];

      for (const [driverId, ticketIds] of grouped.entries()) {
        const driverName = sourceDrivers.find((d) => d.id === driverId)?.name || 'Unknown driver';
        const result = await DriverInvoiceService.createInvoice({
          driverId,
          ticketIds,
          billingType: 'immediate'
        });

        if (!result.success || !result.invoiceId) {
          failures.push(`${driverName}: ${result.error || 'Failed to create invoice'}`);
          continue;
        }

        for (const ticketId of ticketIds) {
          const feeResult = await DriverInvoiceService.addAdminFee(result.invoiceId, ticketId);
          if (!feeResult.success) {
            failures.push(`${driverName}: admin fee could not be added for ticket ${ticketId}`);
          }
        }

        successCount += 1;
      }

      await fetchTickets();
      setSelectedInvoiceTicketIds([]);

      if (successCount > 0) {
        addToast(`Created ${successCount} invoice${successCount > 1 ? 's' : ''} from selected tickets`, 'success');
      }
      if (failures.length > 0) {
        addToast(`Some invoices failed: ${failures[0]}`, 'error');
      }
    } finally {
      setCreatingBulkInvoices(false);
    }
  };

  const openInvoiceModalForTickets = async (ticketIds: string[], mode: 'single' | 'bulk' = 'single') => {
    const context = await loadInvoiceContext();
    if (!context) return;
    const { drivers: contextDrivers, hireMap: contextHireMap } = context;
    const selectedTickets = tickets.filter((ticket) => {
      if (!ticketIds.includes(ticket.id)) return false;
      return RecoveryDecisionService.decideRecoveryForTicket(ticket, {
        drivers: contextDrivers,
        hireDriverByHireId: contextHireMap
      }).invoiceable;
    });

    if (selectedTickets.length === 0) {
      const blocked = tickets
        .filter((ticket) => ticketIds.includes(ticket.id))
        .map((ticket) => RecoveryDecisionService.decideRecoveryForTicket(ticket, {
          drivers: contextDrivers,
          hireDriverByHireId: contextHireMap
        }).blockingReasons[0])
        .filter(Boolean);
      addToast(blocked[0] || 'No eligible tickets selected for invoicing', 'error');
      return;
    }

    const withDrivers = selectedTickets
      .map((ticket) => ({ ticket, driverId: resolveDriverIdForTicket(ticket, contextDrivers, contextHireMap) }))
      .filter((entry) => Boolean(entry.driverId)) as Array<{ ticket: Ticket; driverId: string }>;

    if (withDrivers.length === 0) {
      addToast('Could not resolve driver for selected tickets. Check ticket assignment details.', 'error');
      return;
    }

    if (withDrivers.length !== selectedTickets.length) {
      addToast('Some selected tickets were skipped because no matching driver profile was found.', 'error');
    }

    if (mode === 'bulk') {
      await createBulkInvoicesByDriver(withDrivers, contextDrivers);
      return;
    }

    const uniqueDriverIds = Array.from(new Set(withDrivers.map((entry) => entry.driverId)));
    if (uniqueDriverIds.length !== 1) {
      addToast('Selected ticket could not be mapped to a single driver.', 'error');
      return;
    }

    setInvoicePreselectedDriverId(uniqueDriverIds[0]);
    setInvoicePreselectedTicketIds(withDrivers.map((entry) => entry.ticket.id));
    setIsCreateInvoiceModalOpen(true);
  };

  const handleCreateInvoiceForTicket = async (ticket: Ticket) => {
    await openInvoiceModalForTickets([ticket.id], 'single');
  };

  const handleCreateInvoiceForSelected = async () => {
    await openInvoiceModalForTickets(selectedInvoiceTicketIds, 'bulk');
  };

  // --- Render Helpers ---

  const getStatusColor = (status: string) => {
    switch (normalizeTicketStatus(status)) {
      case 'paid':
        return 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800';
      case 'transferred':
        return 'bg-indigo-100 text-indigo-700 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-800';
      case 'unassigned':
        return 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800';
      case 'active':
        return 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800';
      case 'appeal_pending':
        return 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800';
      case 'cancelled':
        return 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700';
      case 'appeal_won':
        return 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800';
      case 'appeal_lost':
      case 'appeal_rejected':
        return 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-900/40';
      default:
        return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400';
    }
  };

  const getInvoiceBadge = (ticket: Ticket) => {
    if (!ticket.driver_charge_status) return null;
    switch (ticket.driver_charge_status) {
      case 'pending':
        return { label: 'Pending', className: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800' };
      case 'invoiced':
        return { label: 'Invoiced', className: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800' };
      case 'paid':
        return { label: 'Paid', className: 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800' };
      default:
        return null;
    }
  };

  const getActionRequiredReason = (ticket: Ticket) => {
    const status = normalizeTicketStatus(ticket.status);
    const recoveryDecision = RecoveryDecisionService.decideRecoveryForTicket(ticket, {
      drivers,
      hireDriverByHireId
    });

    if (['cancelled', 'appeal_won', 'transferred'].includes(status)) return null;

    if (recoveryDecision.action !== 'none') {
      return {
        label: recoveryDecision.label,
        hint: recoveryDecision.hint
      };
    }

    if (status === 'unassigned') {
      return {
        label: 'Assign Driver',
        hint: 'Ticket is unassigned and requires a driver match.'
      };
    }

    if (status === 'appeal_pending') {
      return {
        label: 'Appeal In Progress',
        hint: 'Appeal is in progress. Keep monitoring until outcome is final.'
      };
    }

    if (status === 'appeal_lost' || status === 'appeal_rejected') {
      return {
        label: 'Payment Required',
        hint: 'Appeal outcome requires payment or recovery action.'
      };
    }

    return {
      label: 'Ticket Not Settled',
      hint: 'Platform-side ticket settlement is not complete yet.'
    };
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  const SortIcon = ({ field }: { field: keyof Ticket }) => {
    if (sortBy !== field) {
      return <ArrowUpDown size={14} className="text-slate-400 ml-1 inline opacity-50 transition-opacity group-hover:opacity-100" />;
    }
    return sortOrder === 'asc' 
      ? <ArrowUp size={14} className="text-primary-600 dark:text-primary-400 ml-1 inline" />
      : <ArrowDown size={14} className="text-primary-600 dark:text-primary-400 ml-1 inline" />;
  };

  const monthOptions = React.useMemo(() => {
    const now = new Date();
    const options: Array<{ value: string; label: string }> = [];
    for (let i = 0; i < 12; i++) {
      const date = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1, 0, 0, 0, 0));
      const year = date.getUTCFullYear();
      const month = date.getUTCMonth() + 1;
      const value = `${year}-${String(month).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    return options;
  }, []);

  const gbpFormatter = React.useMemo(
    () =>
      new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        maximumFractionDigits: 0
      }),
    []
  );

  const getEntryDate = (ticket: Ticket) =>
    String(ticket.created_at || ticket.issue_date || ticket.contravention_date || '');

  const getEntryMonthKey = (ticket: Ticket) => {
    const entry = getEntryDate(ticket);
    const dt = new Date(entry);
    if (Number.isNaN(dt.getTime())) return 'unknown';
    const y = dt.getUTCFullYear();
    const m = String(dt.getUTCMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
  };

  const monthLabelFromKey = (key: string) => {
    if (key === 'unknown') return 'Unknown month';
    const match = /^(\d{4})-(\d{2})$/.exec(key);
    if (!match) return key;
    const year = Number(match[1]);
    const monthIndex = Number(match[2]) - 1;
    const dt = new Date(Date.UTC(year, monthIndex, 1, 0, 0, 0, 0));
    if (Number.isNaN(dt.getTime())) return key;
    return dt.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  };

  const isCompletedStatus = (status: string) => {
    const s = normalizeTicketStatus(status);
    return ['paid', 'transferred', 'appeal_won', 'appeal_lost', 'appeal_rejected', 'cancelled'].includes(s);
  };

  const groupedTicketMonths = React.useMemo(() => {
    if (!shouldGroupByMonth) return [];
    const groups: Array<{ key: string; label: string; tickets: Ticket[]; totalAmount: number; completedCount: number }> = [];
    const indexByKey = new Map<string, number>();

    for (const ticket of tickets) {
      const key = getEntryMonthKey(ticket);
      const idx = indexByKey.get(key);
      if (idx === undefined) {
        indexByKey.set(key, groups.length);
        groups.push({
          key,
          label: monthLabelFromKey(key),
          tickets: [ticket],
          totalAmount: Number(ticket.amount) || 0,
          completedCount: isCompletedStatus(ticket.status) ? 1 : 0
        });
      } else {
        const group = groups[idx];
        group.tickets.push(ticket);
        group.totalAmount += Number(ticket.amount) || 0;
        if (isCompletedStatus(ticket.status)) group.completedCount += 1;
      }
    }

    return groups;
  }, [shouldGroupByMonth, tickets]);

  const isActionRequiredView = activeTab === 'actionRequired';
  const tableColumnCount = isActionRequiredView ? 8 : 7;
  const eligibleInvoiceTicketIds = React.useMemo(
    () => tickets.filter(canInvoiceTicket).map((ticket) => ticket.id),
    [tickets, canInvoiceTicket]
  );
  const eligibleInvoiceTicketIdSet = React.useMemo(
    () => new Set(eligibleInvoiceTicketIds),
    [eligibleInvoiceTicketIds]
  );
  const selectedEligibleCount = selectedInvoiceTicketIds.filter((id) => eligibleInvoiceTicketIdSet.has(id)).length;
  const allEligibleSelected = eligibleInvoiceTicketIds.length > 0 && selectedEligibleCount === eligibleInvoiceTicketIds.length;

  useEffect(() => {
    setSelectedInvoiceTicketIds((prev) => prev.filter((id) => eligibleInvoiceTicketIdSet.has(id)));
  }, [eligibleInvoiceTicketIdSet]);

  const toggleTicketInvoiceSelection = (ticketId: string) => {
    setSelectedInvoiceTicketIds((prev) =>
      prev.includes(ticketId) ? prev.filter((id) => id !== ticketId) : [...prev, ticketId]
    );
  };

  const toggleSelectAllInvoiceTickets = () => {
    if (allEligibleSelected) {
      setSelectedInvoiceTicketIds([]);
      return;
    }
    setSelectedInvoiceTicketIds(eligibleInvoiceTicketIds);
  };

  // Calculate appeal deadline and urgency - Phase 1.3
  const getAppealDeadline = (ticket: Ticket) => {
    if (!ticket.issue_date) return null;

    const issuedDate = new Date(ticket.issue_date);
    // UK standard: 28 days for informal appeal from issue date
    const deadlineDate = new Date(issuedDate);
    deadlineDate.setDate(deadlineDate.getDate() + 28);

    const now = new Date();
    const daysRemaining = Math.ceil((deadlineDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    return {
      date: deadlineDate,
      daysRemaining,
      urgency: daysRemaining <= 3 ? 'critical' : daysRemaining <= 7 ? 'warning' : 'normal'
    };
  };

  const renderTicketRow = (ticket: Ticket) => {
    const deadline = getAppealDeadline(ticket);
    const isInvoiceEligible = canInvoiceTicket(ticket);
    const isSelectedForInvoice = selectedInvoiceTicketIds.includes(ticket.id);
    const stageLabel = getStageDisplayLabel(
      ticket.stage,
      inferEnforcementType({
        issuing_authority: ticket.issuing_authority,
        document_type: ticket.document_type,
        document_intent: ticket.document_intent,
        key_phrases: ticket.key_phrases,
        stage: ticket.stage
      })
    );
    const actionRequiredReason = activeTab === 'actionRequired' ? getActionRequiredReason(ticket) : null;

    return (
    <tr
      key={ticket.id}
      className="group cursor-pointer transition-colors hover:bg-slate-50/80 dark:hover:bg-slate-800/45"
      onClick={() => navigate(`/tickets/${ticket.id}`)}
    >
      {isActionRequiredView && (
        <td className="px-3 py-3 align-middle" onClick={(e) => e.stopPropagation()}>
          {isInvoiceEligible ? (
            <input
              type="checkbox"
              checked={isSelectedForInvoice}
              onChange={() => toggleTicketInvoiceSelection(ticket.id)}
              className="rounded border-slate-300 dark:border-slate-600 text-primary-600 focus:ring-primary-500"
              aria-label={`Select ${ticket.pcn_number} for invoice`}
            />
          ) : (
            <span className="text-slate-300 dark:text-slate-600">-</span>
          )}
        </td>
      )}

      {/* Column 1: Identity */}
      <td className="px-4 py-3 align-middle">
        <div className="flex min-w-0 flex-col gap-1">
          <span className="truncate font-bold leading-5 text-slate-950 dark:text-white" title={ticket.pcn_number}>
            {ticket.pcn_number}
          </span>
          <span className="w-fit rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] leading-4 text-slate-600 dark:bg-slate-800 dark:text-slate-400">
            {ticket.vrn}
          </span>
        </div>
      </td>

      {/* Column 2: Context */}
      <td className="px-4 py-3 align-middle">
        <div className="flex max-w-[360px] min-w-0 flex-col gap-1">
          <span className="truncate font-medium leading-5 text-slate-800 dark:text-slate-200" title={ticket.issuing_authority}>
            {ticket.issuing_authority || 'Unknown Authority'}
          </span>
          <span className="flex min-w-0 items-center gap-1 truncate text-xs leading-4 text-slate-500 dark:text-slate-400" title={ticket.location}>
            <MapPin size={10} className="shrink-0" />
            {ticket.location || 'Unknown Location'}
          </span>
        </div>
      </td>

      {/* Column 3: Entry & Dates */}
      <td className="px-4 py-3 align-middle">
        <div className="grid gap-0.5 text-xs leading-4">
          <div className="flex items-center justify-between gap-3">
            <span className="text-slate-400 dark:text-slate-500">Added</span>
            <span className="tabular-nums text-slate-600 dark:text-slate-300">{formatDate(getEntryDate(ticket))}</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-slate-400 dark:text-slate-500">Contravention</span>
            <span className="tabular-nums font-medium text-slate-900 dark:text-slate-100">{formatDate(ticket.contravention_date)}</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-slate-400 dark:text-slate-500">Issued</span>
            <span className="tabular-nums text-slate-700 dark:text-slate-200">{formatDate(ticket.issue_date)}</span>
          </div>
        </div>
      </td>

      {/* Column 4: Status/Stage (Stacked) */}
      <td className="px-4 py-3 align-middle">
        <div className="flex min-w-0 flex-col items-start gap-1">
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${getStatusColor(
              ticket.status
            )}`}
          >
            {formatTicketStatus(ticket.status)}
          </span>
          <span className="max-w-full truncate text-xs leading-4 text-slate-500 dark:text-slate-400" title={stageLabel}>
            {stageLabel}
          </span>

          {actionRequiredReason && (
            <span
              className="inline-flex max-w-full items-center truncate rounded border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-300"
              title={actionRequiredReason.hint}
            >
              {actionRequiredReason.label}
            </span>
          )}

          {/* Appeal Deadline Badge - Phase 1.3 */}
          {deadline && deadline.daysRemaining > 0 && deadline.daysRemaining <= 14 && ticket.status !== 'appeal_pending' && ticket.status !== 'appeal_won' && ticket.status !== 'paid' && (
            <div
              className={`text-[10px] font-bold px-1.5 py-0.5 rounded flex items-center gap-1 w-fit ${
                deadline.urgency === 'critical'
                  ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                  : deadline.urgency === 'warning'
                  ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                  : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
              }`}
              title={`Appeal deadline: ${deadline.date.toLocaleDateString('en-GB')}`}
            >
              <Clock size={10} />
              {deadline.daysRemaining}d
            </div>
          )}
        </div>
      </td>

      {/* Column 5: Amount */}
      <td className="px-4 py-3 text-right align-middle">
        {ticket.discount_amount ? (
          <div className="flex flex-col items-end">
            <span className="font-bold tabular-nums text-slate-900 dark:text-white">£{ticket.discount_amount}</span>
            <span className="text-[10px] tabular-nums text-slate-400 line-through dark:text-slate-500">£{ticket.amount}</span>
          </div>
        ) : (
          <span className="font-bold tabular-nums text-slate-900 dark:text-white">£{ticket.amount}</span>
        )}
      </td>

      {/* Column 6: Invoice Status */}
      <td className="px-4 py-3 text-center align-middle" onClick={(e) => e.stopPropagation()}>
        {(() => {
          const badge = getInvoiceBadge(ticket);
          if (!badge) return <span className="text-slate-300 dark:text-slate-600">-</span>;
          return (
            <button
              onClick={() => ticket.driver_invoice_id && navigate(`/driver-invoices?invoice=${ticket.driver_invoice_id}`)}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${badge.className} hover:opacity-80 transition-opacity`}
              title={ticket.driver_invoice_id ? 'View Invoice' : undefined}
            >
              <Receipt size={10} />
              {badge.label}
            </button>
          );
        })()}
      </td>

      {/* Column 7: Actions */}
      <td className="px-4 py-3 text-right align-middle" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-end gap-1.5">
          {activeTab === 'actionRequired' && normalizeTicketStatus(ticket.status) === 'unassigned' ? (
            <button
              onClick={() => handleOpenAssignModal(ticket)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-2.5 py-1.5 text-xs font-medium text-white shadow-sm transition-colors hover:bg-primary-700"
            >
              <UserCheck size={14} />
              Assign Hire
            </button>
          ) : (
            <>
              {isActionRequiredView && isInvoiceEligible && (
                <button
                  onClick={() => handleCreateInvoiceForTicket(ticket)}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-2.5 py-1.5 text-xs font-medium text-white shadow-sm transition-colors hover:bg-emerald-700"
                >
                  <Receipt size={14} />
                  Invoice
                </button>
              )}
              <button
                onClick={() => navigate(`/tickets/${ticket.id}`)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-colors hover:border-primary-200 hover:text-primary-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:border-primary-900/50 dark:hover:text-primary-400"
                title="View details"
                aria-label={`View details for ${ticket.pcn_number}`}
              >
                <ArrowRight size={14} />
              </button>
            </>
          )}
          <button
            type="button"
            onClick={() => handleOpenDeleteModal(ticket)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-red-200 bg-red-50 text-red-600 transition-colors hover:bg-red-100 dark:border-red-900/50 dark:bg-red-900/10 dark:text-red-400 dark:hover:bg-red-900/20"
            title="Delete ticket"
            aria-label={`Delete ${ticket.pcn_number}`}
          >
            <Trash2 size={14} />
          </button>
        </div>
      </td>
    </tr>
    );
  };

  return (
    <div 
      className="space-y-6 relative min-h-[500px]"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Drag Overlay */}
      {isDragging && (
        <div className="absolute inset-0 z-50 bg-primary-600/10 dark:bg-primary-900/30 border-2 border-dashed border-primary-500 rounded-xl flex items-center justify-center backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl shadow-xl flex flex-col items-center animate-in zoom-in-95 border border-slate-200 dark:border-slate-800">
            <div className="p-4 bg-primary-100 dark:bg-primary-900/30 rounded-full mb-4">
              <UploadCloud className="w-10 h-10 text-primary-600 dark:text-primary-400" />
            </div>
            <h3 className="text-xl font-bold text-slate-800 dark:text-white">Drop files to upload</h3>
            <p className="text-slate-500 dark:text-slate-400 mt-2">AI will verify and classify your tickets.</p>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Tickets</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Manage and track Penalty Charge Notices.</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleUploadClick}
            disabled={trialLocked}
            className={`px-4 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-sm font-medium flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-60 ${!hasBulkUpload && 'opacity-75'}`}
          >
            {trialLocked || !hasBulkUpload ? <Lock size={16} /> : <UploadCloud size={16} />}
            Batch Upload (AI)
          </button>
          <button
            onClick={handleCsvImportClick}
            disabled={isCsvImporting || trialLocked}
            className={`px-4 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-sm font-medium flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-60 ${!hasBulkUpload && 'opacity-75'} disabled:opacity-70`}
          >
            {isCsvImporting ? <Loader2 size={16} className="animate-spin" /> : (trialLocked || !hasBulkUpload ? <Lock size={16} /> : <Upload size={16} />)}
            {isCsvImporting && csvImportProgress?.percent ? `Importing ${csvImportProgress.percent}%` : 'Import CSV'}
          </button>
          <input
            ref={csvInputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={handleCsvFileChange}
          />
          <button
            onClick={handleManualTicketClick}
            disabled={trialLocked}
            className="px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg hover:bg-slate-800 dark:hover:bg-slate-200 transition-colors text-sm font-medium flex items-center gap-2 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-600 dark:disabled:bg-slate-700 dark:disabled:text-slate-300"
          >
            {trialLocked ? <Lock size={16} /> : <FileText size={16} />}
            Add Manual Ticket
          </button>
        </div>
      </div>

      {trialLocked && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-950 shadow-sm dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-100">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-700 dark:text-amber-300" />
              <div>
                <h2 className="text-sm font-bold">{trialLockedByLimit ? '3-PCN trial limit reached' : 'FleetMode trial ended'}</h2>
                <p className="mt-1 text-sm leading-6 text-amber-900 dark:text-amber-100">{trialLockMessage}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => navigate('/reports?tab=trial-snapshot')}
                className="rounded-lg border border-amber-300 bg-white px-3 py-2 text-xs font-bold text-amber-950 transition hover:bg-amber-100 dark:border-amber-700 dark:bg-slate-950 dark:text-amber-100 dark:hover:bg-slate-900"
              >
                View snapshot
              </button>
              <button
                onClick={() => navigate('/pricing')}
                className="rounded-lg bg-amber-900 px-3 py-2 text-xs font-bold text-white transition hover:bg-amber-950 dark:bg-amber-200 dark:text-amber-950 dark:hover:bg-amber-100"
              >
                Choose plan
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="border-b border-slate-200 dark:border-slate-800 flex items-center justify-between gap-4">
        <nav className="flex gap-4">
          <button
            onClick={() => { setActiveTab('actionRequired'); setPage(1); }}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
              activeTab === 'actionRequired'
                ? 'border-primary-600 text-primary-600 dark:text-primary-400 dark:border-primary-400'
                : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
            }`}
          >
            <AlertTriangle size={16} />
            Action Required
          </button>
          <button
            onClick={() => { setActiveTab('all'); setPage(1); }}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'all'
                ? 'border-primary-600 text-primary-600 dark:text-primary-400 dark:border-primary-400'
                : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
            }`}
          >
            All Tickets
          </button>
        </nav>
        <div className="flex items-center gap-2">
          {isActionRequiredView && (
            <button
              type="button"
              onClick={handleCreateInvoiceForSelected}
              disabled={selectedEligibleCount === 0 || loadingInvoiceContext || creatingBulkInvoices}
              className="px-3 py-2 rounded-lg border text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed"
              title={selectedEligibleCount > 0 ? 'Create invoice for selected tickets' : 'Select one or more eligible tickets'}
            >
              <Receipt size={16} />
              {loadingInvoiceContext ? 'Loading…' : creatingBulkInvoices ? 'Creating…' : `Invoice Selected (${selectedEligibleCount})`}
            </button>
          )}
          <button
            type="button"
            onClick={() => { setGroupByMonth((v) => !v); setPage(1); }}
            className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${
              groupByMonth
                ? 'bg-primary-600 text-white border-primary-600 hover:bg-primary-700'
                : 'bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800'
            }`}
            title="Group tickets by entry month"
          >
            <Layers size={16} />
            {groupByMonth ? 'Grouped by month' : 'Group by month'}
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg border border-red-200 dark:border-red-900 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium">Error loading tickets</h3>
            <p className="text-sm mt-1">{error}</p>
          </div>
        </div>
      )}

      {activeTab === 'all' && (
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by PCN or VRN..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
              className="w-full pl-9 pr-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
            />
          </div>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            <div className="relative w-full sm:w-48">
              <Filter className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <select 
                className="w-full pl-9 pr-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none appearance-none bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                value={filterStatus}
                onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
              >
                <option value="All">All Statuses</option>
                <option value="active">Active</option>
                <option value="unassigned">Unassigned</option>
                <option value="Appeal Pending">Appeal Pending</option>
                <option value="Paid">Paid</option>
                <option value="Transferred">Transferred</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
            
            <div className="relative w-full sm:w-48">
              <Layers className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <select 
                className="w-full pl-9 pr-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none appearance-none bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                value={filterStage}
                onChange={(e) => { setFilterStage(e.target.value); setPage(1); }}
              >
                <option value="All">All Stages</option>
                <option value="NtO/Postal PCN">NtO / Postal PCN</option>
                <option value="Charge Certificate">Charge Certificate</option>
                <option value="Order for Recovery">Order for Recovery</option>
                <option value="Warrant">Warrant</option>
                <option value="Case Closed">Case Closed</option>
              </select>
            </div>

            <div className="relative w-full sm:w-56">
              <Calendar className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <select
                className="w-full pl-9 pr-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none appearance-none bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                value={monthFilter}
                onChange={(e) => { setMonthFilter(e.target.value); setPage(1); }}
              >
                <option value="all">All months</option>
                <option value="this">This month</option>
                <option value="last">Last month</option>
                {monthOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

          </div>
        </div>
      )}

      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1280px] table-fixed text-left text-sm">
              <colgroup>
                {isActionRequiredView && <col className="w-10" />}
                <col className="w-[150px]" />
                <col className="w-[330px]" />
                <col className="w-[220px]" />
                <col className="w-[185px]" />
                <col className="w-[105px]" />
                <col className="w-[95px]" />
                <col className="w-[155px]" />
              </colgroup>
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:bg-slate-800/50 dark:text-slate-400">
                  {isActionRequiredView && (
                    <th className="px-3 py-3 font-medium">
                      <input
                        type="checkbox"
                        checked={allEligibleSelected}
                        onChange={toggleSelectAllInvoiceTickets}
                        disabled={eligibleInvoiceTicketIds.length === 0}
                        className="rounded border-slate-300 dark:border-slate-600 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
                        aria-label="Select all invoice-eligible tickets"
                      />
                    </th>
                  )}
                  <th onClick={() => handleSort('pcn_number')} className="cursor-pointer select-none px-4 py-3 font-medium hover:bg-slate-100 dark:hover:bg-slate-800">Ticket <SortIcon field="pcn_number" /></th>
                  <th onClick={() => handleSort('issuing_authority')} className="cursor-pointer select-none px-4 py-3 font-medium hover:bg-slate-100 dark:hover:bg-slate-800">Location & Authority <SortIcon field="issuing_authority" /></th>
                  <th onClick={() => handleSort('created_at')} className="cursor-pointer select-none px-4 py-3 font-medium hover:bg-slate-100 dark:hover:bg-slate-800">Entry / Dates <SortIcon field="created_at" /></th>
                  <th onClick={() => handleSort('status')} className="cursor-pointer select-none px-4 py-3 font-medium hover:bg-slate-100 dark:hover:bg-slate-800">Stage <SortIcon field="status" /></th>
                  <th onClick={() => handleSort('amount')} className="cursor-pointer select-none px-4 py-3 text-right font-medium hover:bg-slate-100 dark:hover:bg-slate-800">Amount <SortIcon field="amount" /></th>
                  <th className="px-4 py-3 text-center font-medium">Invoice</th>
                  <th className="px-4 py-3 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {tickets.length > 0 ? (
                  shouldGroupByMonth ? (
                    groupedTicketMonths.map((group) => (
                      <React.Fragment key={group.key}>
                        <tr className="bg-slate-50/80 dark:bg-slate-800/60">
                          <td colSpan={tableColumnCount} className="px-4 py-2.5">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                              <div className="font-semibold text-slate-900 dark:text-white">{group.label}</div>
                              <div className="text-xs text-slate-500 dark:text-slate-400">
                                <span className="tabular-nums">{group.tickets.length}</span> ticket(s) •{' '}
                                <span className="tabular-nums">{group.completedCount}</span> completed • Total{' '}
                                <span className="tabular-nums">{gbpFormatter.format(group.totalAmount)}</span>
                              </div>
                            </div>
                          </td>
                        </tr>
                        {group.tickets.map(renderTicketRow)}
                      </React.Fragment>
                    ))
                  ) : (
                    tickets.map(renderTicketRow)
                  )
                ) : (
                  <tr>
                    <td colSpan={tableColumnCount} className="px-6 py-12 text-center text-slate-500 dark:text-slate-400">
                      <div className="flex flex-col items-center justify-center">
                        {activeTab === 'actionRequired' ? (
                          <>
                            <Check className="w-12 h-12 text-green-500 bg-green-50 dark:bg-green-900/20 rounded-full p-3 mb-3" />
                            <p className="font-medium text-slate-900 dark:text-white">All caught up!</p>
                            <p className="text-sm mt-1">No action required tickets found.</p>
                          </>
                        ) : (
                          <>
                            <FileText className="w-12 h-12 text-slate-300 dark:text-slate-600 mb-3" />
                            <p className="font-medium text-slate-900 dark:text-white">No tickets found</p>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {totalCount > limit && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/30">
              <div className="text-sm text-slate-500 dark:text-slate-400">
                Showing <span className="font-medium">{((page - 1) * limit) + 1}</span> to <span className="font-medium">{Math.min(page * limit, totalCount)}</span> of <span className="font-medium">{totalCount}</span> results
              </div>
              <div className="flex gap-2">
                <button 
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed dark:text-slate-300"
                >
                  <ChevronLeft size={16} />
                </button>
                <button 
                  onClick={() => setPage(p => p + 1)}
                  disabled={page * limit >= totalCount}
                  className="p-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed dark:text-slate-300"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <CreateInvoiceModal
        isOpen={isCreateInvoiceModalOpen}
        onClose={() => {
          setIsCreateInvoiceModalOpen(false);
          setInvoicePreselectedDriverId(undefined);
          setInvoicePreselectedTicketIds([]);
        }}
        drivers={drivers}
        preselectedDriverId={invoicePreselectedDriverId}
        preselectedTicketIds={invoicePreselectedTicketIds}
        onSuccess={async () => {
          setIsCreateInvoiceModalOpen(false);
          setInvoicePreselectedDriverId(undefined);
          setInvoicePreselectedTicketIds([]);
          setSelectedInvoiceTicketIds([]);
          await fetchTickets();
          addToast('Driver invoice created successfully', 'success');
        }}
      />

      {/* Assign Hire Modal using reusable Component */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Assign Ticket"
        maxWidth="max-w-2xl"
      >
        {selectedTicket && (
          <div className="p-6">
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-900 rounded-lg p-4 mb-6 grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-1">Vehicle</p>
                <div className="flex items-center gap-2">
                  <Car size={16} className="text-blue-500" />
                  <span className="font-bold text-slate-800 dark:text-white">{selectedTicket.vrn}</span>
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-1">Contravention Date</p>
                <div className="flex items-center gap-2">
                  <Calendar size={16} className="text-blue-500" />
                  <span className="font-medium text-slate-800 dark:text-white">{formatDate(selectedTicket.contravention_date || selectedTicket.issue_date)}</span>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-sm font-bold text-slate-800 dark:text-white mb-3 flex items-center gap-2">
                <UserCheck size={16} className="text-primary-600" />
                Suggested Matches
              </h3>
              
              {loadingHires ? (
                <div className="py-4 flex justify-center">
                  <Loader2 className="animate-spin text-slate-400" />
                </div>
              ) : suggestedHires.length > 0 ? (
                <div className="space-y-3">
                  {suggestedHires.map(hire => (
                    <div key={hire.id} className="border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 rounded-lg p-4 flex items-center justify-between hover:shadow-sm transition-shadow">
                      <div>
                        <p className="font-bold text-slate-800 dark:text-white">{hire.driver_name}</p>
                        <div className="flex items-center gap-2 text-xs mt-1">
                          {'match_score' in (hire as any) && (
                            <span
                              className={`px-2 py-0.5 rounded-full border ${
                                (hire as any).match_score >= 90
                                  ? 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800'
                                  : (hire as any).match_score >= 70
                                    ? 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800'
                                    : 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700'
                              }`}
                              title="Match score"
                            >
                              {(hire as any).match_score}%
                            </span>
                          )}
                          {'match_label' in (hire as any) && (
                            <span className="text-slate-500 dark:text-slate-400">
                              {(hire as any).match_label}
                            </span>
                          )}
                          {'compliance_status' in (hire as any) && (
                            <span
                              className={`px-2 py-0.5 rounded-full border ${
                                (hire as any).compliance_status === 'Compliant'
                                  ? 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800'
                                  : (hire as any).compliance_status === 'At Risk'
                                    ? 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800'
                                    : 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800'
                              }`}
                              title="Agreement compliance"
                            >
                              Agreement: {(hire as any).compliance_status}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-slate-600 dark:text-slate-400 mt-1">
                          {hire.hire_ref && <span>Ref: {hire.hire_ref}</span>}
                          <span>{new Date(hire.start_date).toLocaleDateString('en-GB')} - {hire.end_date ? new Date(hire.end_date).toLocaleDateString('en-GB') : 'Ongoing'}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleAssignHire(hire)}
                        disabled={assigning}
                        className="px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded hover:bg-green-700 transition-colors flex items-center gap-1"
                      >
                        {assigning ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                        Assign
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-100 dark:border-slate-700 border-dashed">
                  <p className="text-sm text-slate-500 dark:text-slate-400">No automatic matches found.</p>
                </div>
              )}
            </div>

            <div className="border-t border-slate-100 dark:border-slate-800 my-4"></div>

            <div>
              <h3 className="text-sm font-bold text-slate-800 dark:text-white mb-3">Search Other Hires</h3>
              <div className="relative mb-3">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by driver name..."
                  value={hireSearchTerm}
                  onChange={(e) => handleSearchHires(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white dark:bg-slate-900 dark:text-white"
                />
              </div>

              {otherHires.length > 0 && (
                <div className="space-y-2 mb-4">
                  {otherHires.map(hire => (
                    <div key={hire.id} className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800">
                      <div>
                        <p className="font-medium text-slate-800 dark:text-white text-sm">{hire.driver_name}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{new Date(hire.start_date).toLocaleDateString('en-GB')} - {hire.end_date ? new Date(hire.end_date).toLocaleDateString('en-GB') : 'Ongoing'}</p>
                      </div>
                      <button
                        onClick={() => handleAssignHire(hire)}
                        disabled={assigning}
                        className="text-primary-600 dark:text-primary-400 hover:text-primary-800 text-xs font-medium"
                      >
                        Select
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="bg-slate-50 dark:bg-slate-800 p-3 rounded-lg flex items-center justify-between">
                <span className="text-xs text-slate-500 dark:text-slate-400">Driver not found?</span>
                <button 
                  onClick={() => {
                    setIsModalOpen(false);
                    navigate('/hires');
                  }}
                  className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 flex items-center gap-1"
                >
                  Create new hire <ArrowRight size={12} />
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => { if (!isDeleting) setIsDeleteModalOpen(false); }}
        title="Delete Ticket"
        maxWidth="max-w-md"
      >
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4 text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg border border-amber-100 dark:border-amber-900">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm font-medium">This action cannot be undone.</p>
          </div>
          <p className="text-slate-600 dark:text-slate-300 mb-6">
            Are you sure you want to delete ticket <span className="font-bold text-slate-900 dark:text-white">{ticketToDelete?.pcn_number}</span>?
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setIsDeleteModalOpen(false)}
              disabled={isDeleting}
              className="px-4 py-2 text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg font-medium transition-colors disabled:opacity-70"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2 disabled:opacity-70"
            >
              {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 size={16} />}
              Delete Ticket
            </button>
          </div>
        </div>
      </Modal>

      <TicketUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onSuccess={() => {
          void fetchTickets();
          void refreshTrialState();
        }}
        droppedFiles={droppedFiles}
      />

      <ManualTicketModal
        isOpen={isManualTicketModalOpen}
        onClose={() => setIsManualTicketModalOpen(false)}
        onCreated={(_ticket) => {
          void fetchTickets();
          void refreshTrialState();
        }}
        onViewTicket={(ticketId) => navigate(`/tickets/${ticketId}`)}
      />

      <Modal
        isOpen={isCsvReportOpen}
        onClose={() => setIsCsvReportOpen(false)}
        title="CSV Import Results"
        maxWidth="max-w-2xl"
      >
        <div className="p-6 space-y-4">
          {isCsvImporting && csvImportProgress && (
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-4">
              <div className="flex items-center justify-between text-sm text-slate-700 dark:text-slate-200 mb-2">
                <span className="font-medium">{csvImportProgress.message}</span>
                <span className="tabular-nums">{csvImportProgress.percent}%</span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                <div className="h-full bg-primary-600" style={{ width: `${csvImportProgress.percent}%` }} />
              </div>
            </div>
          )}
          <div className="flex items-start gap-3 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-800 rounded-lg p-4">
            <Upload className="w-5 h-5 text-primary-600 dark:text-primary-400 mt-0.5" />
            <div>
              <div className="font-medium text-slate-800 dark:text-white">
                Imported {csvImportReport?.success ?? 0} ticket(s)
                {(csvImportReport?.merged ?? 0) > 0 ? `, merged ${csvImportReport?.merged} duplicate(s)` : ''}
              </div>
              <div className="text-sm text-slate-600 dark:text-slate-300">
                {csvImportReport?.errors?.length ? `${csvImportReport.errors.length} issue(s) found.` : 'No issues found.'}
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Required headers: VRM, Date, Location, Amount (optional: PCN, Authority, Issue Date)
              </div>
            </div>
          </div>

          {!!csvImportReport?.errors?.length && (
            <div className="space-y-2">
              <div className="text-sm font-medium text-slate-700 dark:text-slate-200">Issues</div>
              <div className="max-h-72 overflow-auto rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
                <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                  {csvImportReport.errors.slice(0, 150).map((msg, i) => (
                    <li key={i} className="px-4 py-2 text-sm text-slate-700 dark:text-slate-200">
                      {msg}
                    </li>
                  ))}
                </ul>
              </div>
              {csvImportReport.errors.length > 150 && (
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  Showing first 150 issues.
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button
              onClick={() => setIsCsvReportOpen(false)}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Tickets;
